import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, PageHeader, Modal, Button, Field } from "@/components/ui-kit";
import { AlertTriangle, Users, Trash2, ArrowRightLeft, RefreshCw } from "lucide-react";

// Iter v34av (#3) — Recovery Data Absensi: kelola orphan attendance rows
// (rows dengan employee_id yang sudah dihapus atau soft-deleted). Admin bisa:
//   • Reassign — pindahkan ke karyawan aktif (mis: sama orang, re-register)
//   • Delete — hapus permanen bila record tidak dibutuhkan
export default function AttendanceRecovery() {
  const [data, setData] = useState({ groups: [], total_orphan_rows: 0, total_orphan_employees: 0 });
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState(null);
  const [action, setAction] = useState("delete");
  const [targetId, setTargetId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance/orphans");
      setData(r.data || { groups: [], total_orphan_rows: 0, total_orphan_employees: 0 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat orphan data"); }
    setLoading(false);
  };
  const loadEmps = async () => {
    try { const r = await api.get("/employees"); setEmployees(r.data || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); loadEmps(); }, []);

  const openAction = (g) => { setActiveGroup(g); setAction("delete"); setTargetId(""); };

  const doAction = async () => {
    if (!activeGroup) return;
    if (action === "reassign" && !targetId) { toast.error("Pilih karyawan target"); return; }
    const confirm = action === "delete"
      ? `HAPUS PERMANEN ${activeGroup.row_count} baris absensi untuk ${activeGroup.employee_name}?\n\nTindakan ini TIDAK BISA dibatalkan.`
      : `Pindahkan ${activeGroup.row_count} baris absensi dari ${activeGroup.employee_name} ke karyawan yang dipilih?`;
    if (!window.confirm(confirm)) return;
    setBusy(true);
    try {
      const r = await api.post(`/attendance/orphans/${activeGroup.employee_id}/action`, {
        action, target_employee_id: action === "reassign" ? targetId : null,
      });
      toast.success(action === "delete" ? `${r.data.deleted} baris dihapus` : `${r.data.moved} baris dipindahkan`);
      setActiveGroup(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader overline="HR · Recovery" title="Recovery Data Absensi"
        description="Kelola baris absensi orphan — data absensi dari karyawan yang sudah dihapus atau tidak aktif. Reassign ke karyawan aktif atau hapus permanen."
        icon={AlertTriangle}
        action={<Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" />Muat Ulang</Button>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500">Total Karyawan Orphan</div>
          <div className="text-3xl font-bold text-red-700" data-testid="orphan-emp-count">{data.total_orphan_employees}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-stone-500">Total Baris Absensi Orphan</div>
          <div className="text-3xl font-bold text-red-700" data-testid="orphan-row-count">{data.total_orphan_rows}</div>
        </Card>
      </div>

      <Card>
        <div className="p-3 border-b border-stone-100 text-xs uppercase tracking-wider text-stone-600 font-semibold">Daftar Orphan (per karyawan)</div>
        {loading ? (
          <div className="p-8 text-center text-stone-400 text-sm">Memuat…</div>
        ) : data.groups.length === 0 ? (
          <div className="p-8 text-center text-emerald-700 text-sm">
            <b>Bersih!</b> Tidak ada baris absensi orphan.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
              <tr>
                <th className="text-left py-2 px-3">Karyawan</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Departemen</th>
                <th className="text-right py-2 px-3">Baris</th>
                <th className="text-right py-2 px-3">Jam Kerja</th>
                <th className="text-right py-2 px-3">Lembur</th>
                <th className="text-left py-2 px-3">Periode</th>
                <th className="text-right py-2 px-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <tr key={g.employee_id} className="border-b border-stone-100" data-testid={`orphan-row-${g.employee_id}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium">{g.employee_name}</div>
                    <div className="text-[10px] text-stone-500 font-mono">{g.employee_code || g.employee_id.slice(0, 8)}</div>
                  </td>
                  <td className="py-2 px-3 text-xs">
                    <span className={`px-1.5 py-0.5 uppercase font-semibold ${g.kind === "soft_deleted" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                      {g.kind === "soft_deleted" ? "Soft-Deleted" : "Terhapus Total"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-stone-600">{g.department_category || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono">{g.row_count}</td>
                  <td className="py-2 px-3 text-right font-mono">{g.total_work_hours.toFixed(1)}</td>
                  <td className="py-2 px-3 text-right font-mono">{g.total_overtime_hours.toFixed(1)}</td>
                  <td className="py-2 px-3 text-xs text-stone-500">{g.first_date} → {g.last_date}</td>
                  <td className="py-2 px-3 text-right">
                    <Button variant="outline" onClick={() => openAction(g)}
                      className="!text-xs"
                      data-testid={`orphan-action-${g.employee_id}`}>Aksi</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={!!activeGroup} onClose={() => setActiveGroup(null)} title={activeGroup ? `Aksi untuk ${activeGroup.employee_name}` : ""}>
        {activeGroup && (
          <div className="space-y-3">
            <div className="text-xs text-stone-600 border border-stone-200 p-2 bg-stone-50/40">
              <b>{activeGroup.row_count}</b> baris absensi · <b>{activeGroup.total_work_hours}j</b> kerja · <b>{activeGroup.total_overtime_hours}j</b> lembur · periode {activeGroup.first_date} → {activeGroup.last_date}
            </div>
            <Field label="Pilih Aksi">
              <div className="flex gap-2 flex-wrap">
                <label className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm ${action === "delete" ? "border-red-400 bg-red-50" : "border-stone-300"}`}>
                  <input type="radio" checked={action === "delete"} onChange={() => setAction("delete")} data-testid="orphan-action-delete" />
                  <Trash2 className="h-3.5 w-3.5" /> Hapus Permanen
                </label>
                <label className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm ${action === "reassign" ? "border-blue-400 bg-blue-50" : "border-stone-300"}`}>
                  <input type="radio" checked={action === "reassign"} onChange={() => setAction("reassign")} data-testid="orphan-action-reassign" />
                  <ArrowRightLeft className="h-3.5 w-3.5" /> Reassign ke Karyawan Lain
                </label>
              </div>
            </Field>
            {action === "reassign" && (
              <Field label="Pindahkan ke Karyawan">
                <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
                  className="w-full border border-stone-300 px-3 py-2 text-sm bg-white outline-none focus:border-terra-500"
                  data-testid="orphan-target-select">
                  <option value="">— Pilih —</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.employee_code || e.id.slice(0, 8)})</option>
                  ))}
                </select>
              </Field>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
              <Button variant="ghost" onClick={() => setActiveGroup(null)}>Batal</Button>
              <Button onClick={doAction} disabled={busy} data-testid="orphan-do-action"
                className={action === "delete" ? "!bg-red-600 hover:!bg-red-700" : ""}>
                {busy ? "Memproses…" : (action === "delete" ? "Hapus Permanen" : "Reassign")}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
